import "theme/themeInitializer";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import configuredStore from "store/store";
import { I18nextProvider } from "react-i18next";
import i18n from "services/i18n/i18n";
import App from "./App";
import { SnackbarProvider } from "notistack";
import 'react-toastify/dist/ReactToastify.css'
import * as Sentry from "@sentry/react";

import { MaterialUIControllerProvider } from "context";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "components/ErrorFallback";
import { URLParamKey } from "constants/urlParams"
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents"
import { purgeExpiredSessions, checkStoragePersistence } from "services/sessionPersistence"

// Remove stale session data before React mounts (30 min TTL)
purgeExpiredSessions()

const storageStatus = checkStoragePersistence()

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || "development";

const appVersion = import.meta.env.VITE_VERSION || 'unknown'

const getUrlParam = (key: string): string | null => {
  const normalizedKey = key.toLowerCase()
  for (const [k, v] of new URLSearchParams(globalThis.location.search).entries()) {
    if (k.toLowerCase() === normalizedKey) return v
  }
  return null
}

const sessionId = getUrlParam(URLParamKey.SESSION_ID) || 'unknown'
const testType = getUrlParam(URLParamKey.TEST_TYPE) || 'unknown'

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    initialScope: {
      tags: {
        app_version: appVersion,
        sessionId,
        testType,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    // Allow sending PII data including URLs
    sendDefaultPii: true,
    // Prevent Sentry from filtering/scrubbing our image URLs
    beforeSend(event) {
      // Return the event unmodified - no filtering
      return event;
    },
  });
  
  // Send test log to validate Sentry is working and logging to correct project
  Sentry.captureEvent({
    message: "App Started - Sentry Integration Test",
    level: "info",
    tags: {
      event_type: "app_startup",
      test: "true",
    },
    extra: {
      timestamp: new Date().toISOString(),
      environment: sentryEnvironment,
      userAgent: navigator.userAgent,
    },
  });
  console.log("✓ Sentry initialized and test event sent. Check your Sentry dashboard to verify.")

  // Report localStorage reliability for WebView persistence monitoring
  Sentry.setTag(SENTRY_EVENT_TYPES.STORAGE_STATUS, storageStatus)
} else {
  console.warn("Sentry DSN not configured. Set VITE_SENTRY_DSN environment variable to enable error tracking.");
  // Same storage_status signal when Sentry is disabled (dev / missing DSN)
  console.info(`[sessionPersistence] Storage status: ${storageStatus}`)
}

const container = document.getElementById("root");
if (!container) {
  // This error occurs before React can render, so we still need to throw
  // Error Boundaries only catch errors during rendering, not setup errors
  throw new Error("Failed to find the root element");
}

const root = ReactDOM.createRoot(container);
root.render(
  <ErrorBoundary
    FallbackComponent={ErrorFallback}
  >
    <SnackbarProvider
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
      <Provider store={configuredStore}>
        <BrowserRouter>
          <MaterialUIControllerProvider>
            <I18nextProvider i18n={i18n}>
              <App />
            </I18nextProvider>
          </MaterialUIControllerProvider>
        </BrowserRouter>
      </Provider>
    </SnackbarProvider>
  </ErrorBoundary>
);
